import { useRef, useState } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { LockClosedIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import {
  META_VIEW_ADDRESS,
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_META_GUIDE_STEPS,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import { formatCaptureDate } from './WhatsAppConnectionWizard.functions';
import type {
  tWizardMetaGuideStep,
  tWizardMetaViewItem,
} from './WhatsAppConnectionWizard.types';

type tWhatsAppConnectionWizardMetaGuideProps = {
  path: tEmbeddedSignupPath;
};

const META_VIEW_TEXT_CLASS_NAME =
  'text-[length:var(--ls-caption)] text-[var(--ls-text-neutral-default)]';

/**
 * The aside while Meta's window is open: the Meta screens of the chosen path,
 * one at a time, each with its hint and a capture or a built example of the
 * screen, so the person can find the one in front of them. It opens on the first screen
 * every time it mounts, which is every new attempt.
 *
 * @param {tWhatsAppConnectionWizardMetaGuideProps} props - The chosen path.
 * @returns {JSX.Element} The step pager.
 */
export default function WhatsAppConnectionWizardMetaGuide(
  props: tWhatsAppConnectionWizardMetaGuideProps,
) {
  const { path } = props;
  const { t, i18n } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const steps = WIZARD_META_GUIDE_STEPS[path];
  const [shownIndex, setShownIndex] = useState(0);
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const shownStep = steps[shownIndex];
  const lastIndex = steps.length - 1;
  const stepKeyPrefix = `wizard.metaSteps.${shownStep.key}`;

  // At either end the pressed button leaves, so focus goes to the other one
  // before it does.
  function showPrevious() {
    if (shownIndex - 1 === 0) nextButtonRef.current?.focus();
    setShownIndex(shownIndex - 1);
  }

  function showNext() {
    if (shownIndex + 1 === lastIndex) previousButtonRef.current?.focus();
    setShownIndex(shownIndex + 1);
  }

  function renderViewItem(item: tWizardMetaViewItem, index: number) {
    const key = `${item.kind}-${index}`;
    const text = item.textKey ? t(`wizard.metaGuide.view.${item.textKey}`) : '';
    switch (item.kind) {
      case 'title':
        return (
          <p
            key={key}
            className="text-[length:var(--ls-body)] font-medium text-[var(--ls-text-neutral-default)]"
          >
            {text}
          </p>
        );
      case 'caption':
        return (
          <p key={key} className={WIZARD_CAPTION_CLASS_NAME}>
            {text}
          </p>
        );
      case 'label':
        return (
          <p
            key={key}
            className={classNames(META_VIEW_TEXT_CLASS_NAME, 'font-medium')}
          >
            {text}
          </p>
        );
      case 'field':
        return (
          <span
            key={key}
            className={classNames(
              META_VIEW_TEXT_CLASS_NAME,
              'rounded-[var(--ls-radius-sm)] border border-[var(--ls-border-neutral-light)] px-[var(--ls-spacing-2)] py-[var(--ls-spacing-1)]',
            )}
          >
            {text}
          </span>
        );
      case 'radio':
        return (
          <span
            key={key}
            className={classNames(
              META_VIEW_TEXT_CLASS_NAME,
              'flex items-center gap-[var(--ls-spacing-2)]',
            )}
          >
            <span
              aria-hidden="true"
              className={classNames(
                'h-3 w-3 shrink-0 rounded-[var(--ls-radius-full)]',
                item.isEmphasized
                  ? 'border-4 border-[var(--ls-border-neutral-default)]'
                  : 'border border-[var(--ls-border-neutral-light)]',
              )}
            />
            {text}
          </span>
        );
      case 'button':
        return (
          <span
            key={key}
            className={classNames(
              META_VIEW_TEXT_CLASS_NAME,
              'self-start rounded-[var(--ls-radius-md)] border px-[var(--ls-spacing-3)] py-[var(--ls-spacing-1)]',
              item.isEmphasized
                ? 'border-[var(--ls-border-neutral-default)] font-medium'
                : 'border-[var(--ls-border-neutral-light)]',
            )}
          >
            {text}
          </span>
        );
      case 'qr':
        return (
          <QrCodeIcon
            key={key}
            className="h-16 w-16 text-[var(--ls-text-neutral-default)]"
            aria-hidden="true"
          />
        );
    }
  }

  // Meta's capture with the part the hint names marked. The light fill
  // multiplies over the capture, so the marked part stays readable.
  function renderScreenshot(
    step: tWizardMetaGuideStep,
    screenshot: NonNullable<tWizardMetaGuideStep['screenshot']>,
  ) {
    const [left, top, width, height] = screenshot.mark;
    return (
      <div className="relative">
        <img
          src={screenshot.src}
          width={screenshot.width}
          height={screenshot.height}
          alt={t(`wizard.metaSteps.${step.key}.screenshotAlt`)}
          loading="lazy"
          className="block h-auto w-full"
        />
        <span
          data-testid="wizard-meta-view-mark"
          aria-hidden="true"
          // eslint-disable-next-line no-restricted-syntax -- the mark's place is a percent of each capture, data no utility class can express
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: `${width}%`,
            height: `${height}%`,
          }}
          className="absolute rounded-[var(--ls-radius-md)] border border-[var(--ls-accent-primary-default)] bg-[var(--ls-accent-primary-lighter)] mix-blend-multiply ring-1 ring-[var(--ls-accent-primary-default)] dark:mix-blend-screen"
        />
      </div>
    );
  }

  // A window with its title bar, showing Meta's capture of the screen or,
  // without one, the screen drawn from Meta's parts. The part the hint names
  // carries the selected mark of a card.
  function renderMetaView(step: tWizardMetaGuideStep) {
    const { screenshot } = step;
    return (
      <div
        data-testid="wizard-meta-view"
        className="overflow-hidden rounded-[var(--ls-radius-lg)] border border-[var(--ls-border-neutral-light)] bg-[var(--ls-surface-elevation-01)]"
      >
        <div className="flex items-center gap-[var(--ls-spacing-2)] border-b border-[var(--ls-border-neutral-light)] px-[var(--ls-spacing-3)] py-[var(--ls-spacing-2)]">
          {[0, 1, 2].map((control) => (
            <span
              key={control}
              data-testid="wizard-meta-view-control"
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-[var(--ls-radius-full)] bg-[var(--ls-border-neutral-light)]"
            />
          ))}
          <LockClosedIcon
            className="ml-[var(--ls-spacing-2)] h-3.5 w-3.5 shrink-0 text-[var(--ls-text-neutral-light)]"
            aria-hidden="true"
          />
          <span className={WIZARD_CAPTION_CLASS_NAME}>{META_VIEW_ADDRESS}</span>
          <span className={classNames(WIZARD_CAPTION_CLASS_NAME, 'ml-auto')}>
            {screenshot
              ? t('wizard.metaGuide.capturedOn', {
                  date: formatCaptureDate({
                    capturedOn: screenshot.capturedOn,
                    language: i18n.language,
                  }),
                })
              : t('wizard.metaGuide.exampleView')}
          </span>
        </div>
        {screenshot ? (
          renderScreenshot(step, screenshot)
        ) : (
          <div className="flex flex-col gap-[var(--ls-spacing-2)] p-[var(--ls-spacing-4)]">
            {step.view.map(renderViewItem)}
            {step.highlighted && (
              <div
                data-highlighted="true"
                className="flex flex-col gap-[var(--ls-spacing-2)] rounded-[var(--ls-radius-md)] border border-[var(--ls-accent-primary-default)] bg-[var(--ls-accent-primary-lighter)] p-[var(--ls-spacing-2)] ring-1 ring-[var(--ls-accent-primary-default)]"
              >
                {step.highlighted.map(renderViewItem)}
              </div>
            )}
            {step.viewAfter?.map(renderViewItem)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-4)]">
      <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
        {t('wizard.metaGuide.subtitle')}
      </p>
      {/* Steps carry no labels and no done state: the person may be on any
          of them, so only the shown one is marked. */}
      <div className="flex gap-[var(--ls-spacing-2)]">
        {steps.map((step, index) => (
          <Button
            key={step.key}
            color="ghost"
            size="xs"
            type="button"
            aria-label={`${t(`wizard.metaGuide.stepPrefix.${index + 1}`)} ${t(`wizard.metaSteps.${step.key}.title`)}`}
            aria-current={index === shownIndex ? 'step' : undefined}
            className="flex-1 px-0"
            onClick={() => setShownIndex(index)}
          >
            <span
              aria-hidden="true"
              className={classNames(
                'h-1.5 w-full rounded-[var(--ls-radius-full)]',
                index === shownIndex
                  ? 'bg-[var(--ls-accent-primary-default)]'
                  : 'bg-[var(--ls-border-neutral-lighter)]',
              )}
            />
          </Button>
        ))}
      </div>
      <div className="flex flex-col gap-[var(--ls-spacing-1)]">
        <p className={WIZARD_CAPTION_CLASS_NAME}>
          {t(`wizard.metaGuide.position.${shownIndex + 1}`)}
        </p>
        <h4 className="text-[length:var(--ls-body-lg)] font-medium text-[var(--ls-text-neutral-default)]">
          {t(`${stepKeyPrefix}.title`)}
        </h4>
        {shownStep.hasNote && (
          <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
            {t(`${stepKeyPrefix}.note`)}
          </p>
        )}
      </div>
      <p className={WIZARD_BODY_CLASS_NAME}>
        <Trans
          t={t}
          i18nKey={`${stepKeyPrefix}.hint`}
          components={{ strong: <strong className="font-medium" /> }}
        />
      </p>
      {renderMetaView(shownStep)}
      <div className="flex justify-end gap-[var(--ls-spacing-3)]">
        {shownIndex > 0 && (
          <Button
            key="previous"
            ref={previousButtonRef}
            color="ghost"
            size="sm"
            type="button"
            onClick={showPrevious}
          >
            {t('wizard.metaGuide.previous')}
          </Button>
        )}
        {/* On the last step "Siguiente" keeps its width, hidden and inert, so
            "Anterior" does not move under the pointer. */}
        {shownIndex === lastIndex ? (
          <Button
            key="nextPlaceholder"
            color="secondary"
            size="sm"
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            disabled
            className="invisible"
          >
            {t('wizard.metaGuide.next')}
          </Button>
        ) : (
          <Button
            key="next"
            ref={nextButtonRef}
            color="secondary"
            size="sm"
            type="button"
            onClick={showNext}
          >
            {t('wizard.metaGuide.next')}
          </Button>
        )}
      </div>
    </div>
  );
}
